import React, { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { dashboardApi } from '../services/api'
import { PageLoader } from '../components/ui'
import useAuthStore from '../stores/authStore'
import { hasSection, isAdmin as esAdmin } from '../config/permissions'

// Panel de inicio. Responde tres preguntas, en este orden:
//   1. ¿A quién busco?            → el buscador, acá mismo.
//   2. ¿Dónde trabajo?            → los módulos del usuario, con sus cifras y acciones directas.
//   3. ¿Qué tengo para resolver?  → pendientes reales de esos módulos.
// Nadie ve módulos ni cifras de áreas a las que no tiene acceso.
// Con pocos módulos cada uno se muestra en grande (cifras, acciones, lo último cargado);
// con muchos, en una grilla compacta.

const n = (v) => Number(v || 0).toLocaleString('es-AR')
const plural = (c, uno, muchos) => (Number(c) === 1 ? uno : muchos)
const dmy = (iso) => (iso ? String(iso).slice(0, 10).split('-').reverse().join('/') : 'Sin fecha')
const MAX_MODULOS_EN_GRANDE = 3

const PENDIENTES = [
  { permiso: 'reclamos', href: '/reclamos', cuenta: (m) => m.reclamos?.pendientes, texto: (c) => plural(c, 'reclamo pendiente', 'reclamos pendientes'), ayuda: 'Vecinos esperando respuesta' },
  { permiso: 'mesa_entrada', href: '/mesa-entrada', cuenta: (m) => m.mesa_entrada?.pendientes, texto: (c) => plural(c, 'trámite sin resolver', 'trámites sin resolver'), ayuda: 'Mesa de Entrada' },
  { permiso: 'vales', href: '/vales', cuenta: (m) => m.vales?.sin_persona_mes, texto: (c) => plural(c, 'vale de este mes sin DNI', 'vales de este mes sin DNI'), ayuda: 'No quedan en la ficha de la persona' },
  { permiso: 'comedores', href: '/comedores', cuenta: (m) => m.comedores?.sin_tutor, texto: (c) => plural(c, 'chico sin tutor/a cargado', 'chicos sin tutor/a cargado'), ayuda: 'Comedores: no se ve su familia' },
  { permiso: 'revision_duplicados', href: '/revision-duplicados', cuenta: (m) => m.revision_duplicados?.pendientes, texto: (c) => plural(c, 'identidad por revisar', 'identidades por revisar'), ayuda: 'Registros sin persona asignada' },
]

// `cifras(m)` → [[valor, etiqueta], …] (la primera es la principal). `acciones` y `recientes` son opcionales.
const MODULOS = [
  {
    permiso: 'capacitaciones', nombre: 'Capacitaciones', href: '/capacitaciones',
    descripcion: 'Registrá cada capacitación y quiénes participaron.',
    cifras: (m) => m.capacitaciones && [[m.capacitaciones.mes, 'este mes'], [m.capacitaciones.total, plural(m.capacitaciones.total, 'registrada', 'registradas')], [m.capacitaciones.personas, plural(m.capacitaciones.personas, 'persona capacitada', 'personas capacitadas')]],
    acciones: [{ texto: '+ Nueva capacitación', href: '/capacitaciones?nueva=1', primaria: true }, { texto: 'Padrón de capacitados', href: '/capacitaciones?vista=padron' }],
    recientes: (m) => (m.capacitaciones?.recientes || []).map((c) => ({
      href: `/capacitaciones?id=${c.id}`, titulo: c.nombre,
      detalle: `${dmy(c.fecha)}${c.area ? ` · ${c.area}` : ''} · ${n(c.participantes)} ${plural(c.participantes, 'participante', 'participantes')}`,
    })),
    tituloRecientes: 'Últimas registradas',
  },
  {
    permiso: 'produccion', nombre: 'Producción', href: '/produccion',
    descripcion: 'Entrega de semillas, plantines y abonos a vecinos.',
    cifras: (m) => m.produccion && [[m.produccion.mes, 'entregas este mes'], [m.produccion.total, plural(m.produccion.total, 'entrega registrada', 'entregas registradas')], [m.produccion.personas, plural(m.produccion.personas, 'vecino atendido', 'vecinos atendidos')]],
    acciones: [{ texto: '+ Cargar entregas', href: '/produccion?nueva=1', primaria: true }, { texto: 'Padrón de productores', href: '/produccion?vista=padron' }],
  },
  { permiso: 'vales', nombre: 'Vales para Retirar', href: '/vales', descripcion: 'Emisión de vales de mercadería, medicamentos y pasajes.', cifras: (m) => m.vales && [[m.vales.mes, 'emitidos este mes'], [m.vales.hoy, 'hoy'], [m.vales.sin_persona_mes, 'sin DNI este mes']] },
  { permiso: 'reclamos', nombre: 'Reclamos', href: '/reclamos', descripcion: 'Alumbrado, residuos, poda y calles.', cifras: (m) => m.reclamos && [[m.reclamos.pendientes, 'pendientes'], [m.reclamos.en_proceso, 'en proceso'], [m.reclamos.sin_identificar, 'sin identificar']] },
  { permiso: 'mesa_entrada', nombre: 'Mesa de Entrada', href: '/mesa-entrada', descripcion: 'Trámites y solicitudes presenciales.', cifras: (m) => m.mesa_entrada && [[m.mesa_entrada.pendientes, 'trámites pendientes'], [m.mesa_entrada.mes, 'ingresados este mes']] },
  { permiso: 'comedores', nombre: 'Comedores', href: '/comedores', descripcion: 'Padrón de chicos y asistencia en cada sede.', cifras: (m) => m.comedores && [[m.comedores.anotados, 'chicos anotados'], [m.comedores.sedes, 'sedes'], [m.comedores.sin_tutor, 'sin tutor/a']] },
  { permiso: 'comedores_admin', nombre: 'Adm. Comedores', href: '/comedores-administracion', descripcion: 'Mercadería, gastos y costo por ración.' },
  { permiso: 'instituciones', nombre: 'Instituciones', href: '/instituciones', descripcion: 'Escuelas, iglesias y clubes: pedidos de recursos.', cifras: (m) => m.instituciones && [[m.instituciones.pedidos_mes, 'pedidos este mes'], [m.instituciones.total, 'instituciones']] },
  { permiso: 'emprendedores', nombre: 'Emprendedores', href: '/emprendedores', descripcion: 'Padrón de emprendedores y ayudas.', cifras: (m) => m.emprendedores && [[m.emprendedores.total, 'en el padrón']] },
  { permiso: 'caja_celiacos', nombre: 'Caja Celíacos', href: '/caja-celiacos', descripcion: 'Entrega de cajas.', cifras: (m) => m.caja_celiacos && [[m.caja_celiacos.activos, 'beneficiarios activos']] },
  { permiso: 'tarjeta_sapucay', nombre: 'Tarjeta Sapucay', href: '/tarjeta-sapucay', descripcion: 'Control de tarjetas.', cifras: (m) => m.tarjeta_sapucay && [[m.tarjeta_sapucay.activos, 'beneficiarios activos']] },
  { permiso: 'tarjeta_mama_mbarete', nombre: 'Mamá Mbareté', href: '/tarjeta-mama-mbarete', descripcion: 'Padrón de embarazadas.', cifras: (m) => m.tarjeta_mama_mbarete && [[m.tarjeta_mama_mbarete.activos, 'beneficiarias activas']] },
  { permiso: 'kit_femenino', nombre: 'Kit Femenino', href: '/kit-femenino', descripcion: 'Planilla mensual de kits (hasta 200 por mes).', cifras: (m) => m.kit_femenino && [[m.kit_femenino.mes, 'kits este mes'], [m.kit_femenino.total, 'kits en total']] },
  { permiso: 'inundaciones', nombre: 'Inundaciones', href: '/inundaciones', descripcion: 'Evacuados y contingencia.', cifras: (m) => m.inundaciones && [[m.inundaciones.evacuados, `evacuados${m.inundaciones.evento ? ` · ${m.inundaciones.evento}` : ''}`]] },
]

const VEHICULOS = [
  { permiso: 'viajes_corrientes', nombre: 'Combi Corrientes', href: '/viajes-corrientes' },
  { permiso: 'viajes_goya', nombre: 'Combi Goya', href: '/viajes-goya' },
  { permiso: 'viajes_camioneta_mn', nombre: 'Camioneta AF802MN', href: '/viajes-camioneta-mn' },
  { permiso: 'viajes_camioneta_mo', nombre: 'Camioneta AF802MO', href: '/viajes-camioneta-mo' },
  { permiso: 'viajes_colectivo', nombre: 'Colectivo', href: '/viajes-colectivo' },
  { permiso: 'viajes_camion', nombre: 'Camión', href: '/viajes-camion' },
  { permiso: 'viajes_captiva', nombre: 'Chevrolet Captiva', href: '/viajes-captiva' },
]

const ATAJOS = [
  { permiso: 'beneficiarios', nombre: 'Padrón de beneficiarios', href: '/beneficiarios' },
  { permiso: 'viajes_padron', nombre: 'Padrón de Viajes', href: '/viajes-padron' },
  { permiso: 'el_nino', nombre: 'Fenómeno El Niño', href: '/el-nino' },
  { permiso: 'whatsapp', nombre: 'WhatsApp', href: '/whatsapp' },
  { permiso: 'analytics', nombre: 'Analytics', href: '/analytics' },
  { permiso: 'revision_duplicados', nombre: 'Revisión de duplicados', href: '/revision-duplicados' },
  { permiso: 'tipos_asistencia', nombre: 'Tipos de asistencia', href: '/admin/tipos-asistencia' },
  { permiso: 'logs', nombre: 'Logs y auditoría', href: '/logs' },
  { permiso: 'admin', nombre: 'Usuarios y permisos', href: '/usuarios' },
  { permiso: null, nombre: 'Soporte técnico', href: '/soporte' },
]

const titulo = 'text-xs font-black uppercase tracking-widest text-slate-700'
const tarjeta = 'rounded-2xl border border-slate-300 bg-white shadow-sm'
const flecha = <span aria-hidden="true" className="font-black text-slate-400 transition group-hover:translate-x-1 group-hover:text-social-700">→</span>

// Módulo en grande: cifras, acciones directas y lo último cargado.
function ModuloGrande({ x }) {
  const acciones = x.acciones || [{ texto: `Abrir ${x.nombre}`, href: x.href, primaria: true }]
  return (
    <article className={`${tarjeta} overflow-hidden`}>
      <header className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-300 bg-slate-50 px-5 py-4">
        <div className="min-w-0">
          <h3 className="text-lg font-black text-ink">{x.nombre}</h3>
          {x.descripcion && <p className="text-sm font-medium text-slate-600">{x.descripcion}</p>}
        </div>
        <div className="flex flex-wrap gap-2">
          {acciones.map((a) => (
            <Link key={a.href} to={a.href} className={a.primaria ? 'btn-social px-5 font-black' : 'btn-outline border-slate-400 font-bold'}>{a.texto}</Link>
          ))}
        </div>
      </header>
      {x.c && (
        <dl className="grid grid-cols-1 divide-y divide-slate-300 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {x.c.map(([valor, etiqueta], i) => (
            <div key={etiqueta} className="px-5 py-4">
              <dd className={`text-4xl font-black tabular-nums ${i === 0 ? 'text-social-700' : 'text-ink'}`}>{n(valor)}</dd>
              <dt className="mt-0.5 text-sm font-bold text-slate-700">{etiqueta}</dt>
            </div>
          ))}
        </dl>
      )}
      {x.r && (
        <div className="border-t border-slate-300 px-5 py-4">
          <h4 className={`${titulo} mb-2`}>{x.tituloRecientes || 'Lo último'}</h4>
          {x.r.length ? (
            <ul className="divide-y divide-slate-200">
              {x.r.map((r) => (
                <li key={r.href}>
                  <Link to={r.href} className="group flex items-center justify-between gap-3 rounded-lg px-2 py-2.5 hover:bg-social-50">
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-black uppercase text-ink">{r.titulo}</span>
                      <span className="block text-xs font-semibold text-slate-600">{r.detalle}</span>
                    </span>
                    {flecha}
                  </Link>
                </li>
              ))}
            </ul>
          ) : <p className="text-sm font-medium text-slate-600">Todavía no hay nada cargado. Empezá con el botón de arriba.</p>}
        </div>
      )}
    </article>
  )
}

function ModuloChico({ nombre, href, c }) {
  return (
    <Link to={href} className={`${tarjeta} group flex flex-col p-4 transition hover:-translate-y-0.5 hover:border-social-700 hover:shadow-md`}>
      <span className="flex items-center justify-between gap-2">
        <span className="text-sm font-black text-ink">{nombre}</span>
        {flecha}
      </span>
      {c ? (
        <>
          <span className="mt-2 flex items-baseline gap-2">
            <span className="text-3xl font-black tabular-nums text-social-700">{n(c[0][0])}</span>
            <span className="text-xs font-bold text-slate-700">{c[0][1]}</span>
          </span>
          {c[1] && <span className="mt-1 text-xs font-semibold text-slate-600">{n(c[1][0])} {c[1][1]}</span>}
        </>
      ) : <span className="mt-2 text-xs font-semibold text-slate-600">Abrir el módulo</span>}
    </Link>
  )
}

export default function Dashboard() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [datos, setDatos] = useState(null)
  const [q, setQ] = useState('')
  const puedeBuscar = hasSection(user, 'buscar')
  const esLector = /^(lector|read_only|readonly)$/.test(String(user?.rol || '').trim().toLowerCase())

  useEffect(() => {
    // El lector solo consulta: su lugar es el buscador.
    if (esLector && puedeBuscar) { navigate('/buscar', { replace: true }); return }
    dashboardApi.getPanel()
      .then((r) => setDatos(r.data.data))
      .catch(() => setDatos({ general: {}, modulos: {}, viajes: {}, error: true }))
  }, [esLector, puedeBuscar, navigate])

  const vista = useMemo(() => {
    if (!datos) return null
    const puede = (permiso) => permiso === null || (permiso === 'admin' ? esAdmin(user) : hasSection(user, permiso))
    const m = datos.modulos || {}
    return {
      pendientes: PENDIENTES.filter((p) => puede(p.permiso)).map((p) => ({ ...p, c: Number(p.cuenta(m) || 0) })).filter((p) => p.c > 0),
      modulos: MODULOS.filter((x) => puede(x.permiso)).map((x) => ({ ...x, c: x.cifras ? x.cifras(m) : null, r: x.recientes ? x.recientes(m) : null })),
      vehiculos: VEHICULOS.filter((v) => puede(v.permiso)).map((v) => ({ ...v, d: datos.viajes?.[v.permiso] || {} })),
      atajos: ATAJOS.filter((a) => puede(a.permiso)),
    }
  }, [datos, user])

  const buscar = (e) => {
    e.preventDefault()
    const texto = q.trim()
    navigate(texto ? `/buscar?q=${encodeURIComponent(texto)}` : '/buscar')
  }

  if (!vista) return <PageLoader />

  const g = datos.general || {}
  const hoy = new Date().toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long' })
  const totalModulos = vista.modulos.length + vista.vehiculos.length
  const enGrande = totalModulos > 0 && totalModulos <= MAX_MODULOS_EN_GRANDE

  return (
    <div className="mx-auto max-w-7xl space-y-6">
      {/* 1. Buscar: lo primero que hace cualquier operador */}
      <section className="overflow-hidden rounded-3xl bg-social-800 text-white shadow-lg">
        <div className="grid gap-6 p-6 sm:p-8 lg:grid-cols-[1fr_auto] lg:items-end">
          <div>
            <p className="text-sm font-bold text-social-100 first-letter:uppercase">{hoy}</p>
            <h1 className="mt-1 text-3xl font-black tracking-tight sm:text-4xl">Hola, {user?.usuario}</h1>
            <p className="mt-1 text-base font-medium text-social-100">Buscá primero a la persona: así no se duplican registros.</p>
            {puedeBuscar && <form onSubmit={buscar} role="search" className="mt-5 flex flex-col gap-2 sm:flex-row">
              <label htmlFor="panel-buscar" className="sr-only">Buscar persona por DNI o nombre</label>
              <input id="panel-buscar" autoFocus value={q} onChange={(e) => setQ(e.target.value)} inputMode="search" autoComplete="off"
                placeholder="DNI, o apellido y nombre"
                className="h-14 w-full flex-1 rounded-xl border-2 border-white bg-white px-5 text-lg font-bold text-ink shadow-inner outline-none placeholder:font-semibold placeholder:text-slate-500 focus:ring-4 focus:ring-social-300" />
              <button type="submit" className="h-14 rounded-xl bg-white px-8 text-lg font-black text-social-800 shadow transition hover:bg-social-50 active:scale-[.98]">Buscar</button>
            </form>}
            <div className="mt-3 flex flex-wrap gap-2">
              <Link to="/asistencia/registrar" className="rounded-xl border-2 border-white/70 px-4 py-2 text-sm font-black text-white transition hover:bg-white hover:text-social-800">+ Nueva asistencia</Link>
              <Link to="/personas/nueva" className="rounded-xl border-2 border-white/70 px-4 py-2 text-sm font-black text-white transition hover:bg-white hover:text-social-800">+ Nueva persona</Link>
            </div>
          </div>
          <dl className="flex gap-3 lg:flex-col">
            <div className="flex-1 rounded-2xl bg-white/10 px-5 py-3 ring-1 ring-white/25">
              <dd className="text-3xl font-black tabular-nums">{n(g.personas)}</dd>
              <dt className="text-xs font-bold uppercase tracking-wide text-social-100">Personas en el padrón</dt>
            </div>
            {Number(g.mias_mes) > 0 && (
              <div className="flex-1 rounded-2xl bg-white/10 px-5 py-3 ring-1 ring-white/25">
                <dd className="text-3xl font-black tabular-nums">{n(g.mias_mes)}</dd>
                <dt className="text-xs font-bold uppercase tracking-wide text-social-100">Asistencias que cargaste este mes</dt>
              </div>
            )}
          </dl>
        </div>
      </section>

      {datos.error && (
        <p role="alert" className="rounded-xl border border-amber-400 bg-amber-50 px-4 py-3 text-sm font-bold text-amber-900">
          No se pudieron cargar las cifras. Los accesos funcionan igual.
        </p>
      )}

      <div className="grid items-start gap-6 lg:grid-cols-3">
        {/* 2. Tus módulos */}
        <section aria-labelledby="t-modulos" className="space-y-3 lg:col-span-2">
          <h2 id="t-modulos" className={titulo}>{plural(totalModulos, 'Tu módulo', 'Tus módulos')}</h2>
          {totalModulos === 0 && (
            <p className={`${tarjeta} px-5 py-6 text-sm font-semibold text-slate-700`}>
              Tu usuario trabaja desde el buscador: buscá a la persona y registrá la asistencia desde su ficha.
              Si necesitás acceso a otro módulo, pedíselo a un administrador.
            </p>
          )}
          {enGrande ? (
            <div className="space-y-4">
              {vista.modulos.map((x) => <ModuloGrande key={x.href} x={x} />)}
              {vista.vehiculos.map((v) => (
                <ModuloGrande key={v.href} x={{ ...v, descripcion: 'Planilla semanal de pasajeros.', c: [[v.d.hoy, plural(v.d.hoy, 'pasajero hoy', 'pasajeros hoy')], [v.d.manana, 'mañana']] }} />
              ))}
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {vista.modulos.map((x) => <ModuloChico key={x.href} nombre={x.nombre} href={x.href} c={x.c} />)}
              {vista.vehiculos.map((v) => (
                <ModuloChico key={v.href} nombre={v.nombre} href={v.href} c={[[v.d.hoy, plural(v.d.hoy, 'pasajero hoy', 'pasajeros hoy')], [v.d.manana, 'mañana']]} />
              ))}
            </div>
          )}
        </section>

        {/* 3. Para resolver + accesos */}
        <aside className="space-y-6">
          <section aria-labelledby="t-pendientes" className="space-y-3">
            <h2 id="t-pendientes" className={titulo}>Para resolver</h2>
            {vista.pendientes.length ? vista.pendientes.map((p) => (
              <Link key={p.href + p.ayuda} to={p.href}
                className="group flex items-center gap-4 rounded-2xl border-2 border-amber-400 bg-amber-50 px-4 py-3 transition hover:-translate-y-0.5 hover:border-amber-600 hover:shadow-md">
                <span className="min-w-[3.5rem] text-right text-3xl font-black tabular-nums text-amber-900">{n(p.c)}</span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-black leading-tight text-ink">{p.texto(p.c)}</span>
                  <span className="block text-xs font-semibold text-slate-700">{p.ayuda}</span>
                </span>
                <span aria-hidden="true" className="text-xl font-black text-amber-800 transition group-hover:translate-x-1">→</span>
              </Link>
            )) : (
              <p className="rounded-2xl border-2 border-green-600 bg-green-50 px-4 py-4 text-sm font-black text-green-900">
                ✓ Nada pendiente en tus módulos.
              </p>
            )}
          </section>

          <nav aria-labelledby="t-accesos" className={`${tarjeta} overflow-hidden`}>
            <h2 id="t-accesos" className={`${titulo} border-b border-slate-300 bg-slate-50 px-4 py-3`}>Otros accesos</h2>
            <ul className="divide-y divide-slate-200">
              {vista.atajos.map((a) => (
                <li key={a.href}>
                  <Link to={a.href} className="group flex items-center justify-between px-4 py-3 text-sm font-bold text-slate-800 hover:bg-social-50 hover:text-social-900">
                    {a.nombre}{flecha}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </aside>
      </div>
    </div>
  )
}
